import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Switch } from "../ui/switch";
import { api, errMsg, PLATFORMS } from "../../lib/api";

const input = "mt-1.5 h-11 w-full border border-white/15 bg-black/40 px-3 font-mono text-sm text-white focus:border-[#a259ff] focus:outline-none";
const lbl = "block font-mono text-[11px] uppercase tracking-widest text-slate-400";
const EMPTY = { code: "", kind: "percent", value: "", max_uses: 0, active: true, expires: "", platforms: [], product_ids: [] };
const toggleIn = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
const endOfDay = (d) => (d ? new Date(`${d}T23:59:59`).toISOString() : null);

export const DiscountsTab = ({ codeStats }) => {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [f, setF] = useState(EMPTY);

  const load = () => api.get("/admin/discounts").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
  useEffect(() => {
    load();
    api.get("/products").then((r) => setProducts(Array.isArray(r.data) ? r.data : [])).catch(() => setProducts([]));
  }, []);

  const create = async (e) => {
    e.preventDefault();
    const { expires, ...rest } = f;
    try {
      await api.post("/admin/discounts", { ...rest, value: Number(f.value), max_uses: Number(f.max_uses), expires_at: endOfDay(expires) });
      toast.success("Code created");
      setF(EMPTY);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const toggle = async (d) => {
    const { id, uses, created_at, ...rest } = d;
    await api.put(`/admin/discounts/${id}`, { ...rest, active: !d.active }).catch((err) => toast.error(errMsg(err)));
    load();
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete code ${d.code}?`)) return;
    await api.delete(`/admin/discounts/${d.id}`).catch((err) => toast.error(errMsg(err)));
    load();
  };

  return (
    <div className="mt-6 grid gap-8 lg:grid-cols-12">
      <form onSubmit={create} className="space-y-4 border border-white/10 bg-[#0f0f16] p-5 lg:col-span-4" data-testid="admin-discount-form">
        <p className="font-display text-3xl uppercase">New promo code</p>
        <label className={lbl}>Code<input required value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} placeholder="OGM10" className={`${input} uppercase`} data-testid="discount-code-input" /></label>
        <div className="grid grid-cols-2 gap-3">
          <label className={lbl}>Type
            <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className={input} data-testid="discount-kind-select">
              <option value="percent">% off</option>
              <option value="fixed">$ off</option>
            </select>
          </label>
          <label className={lbl}>Amount<input required type="number" min="0.01" step="0.01" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} className={input} data-testid="discount-value-input" /></label>
        </div>
        <label className={lbl}>Max uses (0 = unlimited)<input type="number" min="0" value={f.max_uses} onChange={(e) => setF({ ...f, max_uses: e.target.value })} className={input} data-testid="discount-max-input" /></label>
        <label className={lbl}>Ends on (optional, end of day)<input type="date" value={f.expires} onChange={(e) => setF({ ...f, expires: e.target.value })} className={`${input} [color-scheme:dark]`} data-testid="discount-expires-input" /></label>
        <div className={lbl}>Accounts (none ticked = all)
          <div className="mt-2 max-h-40 space-y-1.5 overflow-y-auto">
            {products.map((p) => (
              <label key={p.id} className="flex cursor-pointer items-center gap-2 text-sm normal-case tracking-normal text-slate-200">
                <input type="checkbox" checked={f.product_ids.includes(p.id)} onChange={() => setF({ ...f, product_ids: toggleIn(f.product_ids, p.id) })} className="accent-[#a259ff]" data-testid={`discount-product-${p.id}`} />
                {p.name} <span className="text-slate-400">· {PLATFORMS[p.platform]?.label}</span>
              </label>
            ))}
          </div>
        </div>
        <button className="h-12 w-full bg-[#ff3dc8] font-mono text-xs font-bold uppercase tracking-widest text-white" data-testid="discount-create-button">Create code</button>
      </form>
      <div className="space-y-3 lg:col-span-8">
        {!items.length && <p className="text-slate-400" data-testid="admin-discounts-empty">No promo codes yet.</p>}
        {items.map((d) => (
          <div key={d.id} className="flex flex-wrap items-center gap-4 border border-white/10 bg-[#0f0f16] p-4" data-testid={`admin-discount-row-${d.code}`}>
            <p className="font-mono text-lg font-bold text-[#e9d5ff]">{d.code}</p>
            <p className="flex-1 text-sm text-slate-300">
              {d.kind === "percent" ? `${d.value}% off` : `$${d.value} off`} · used {d.uses}{d.max_uses ? ` / ${d.max_uses}` : ""}
              <span className="block text-xs text-slate-400" data-testid={`discount-limits-${d.code}`}>
                {d.expires_at ? `${new Date(d.expires_at) < new Date() ? "Expired" : "Ends"} ${new Date(d.expires_at).toLocaleDateString()}` : "No end date"}
                {" · "}{d.platforms?.length ? d.platforms.map((p) => PLATFORMS[p]?.label).join(", ") : "All platforms"}
                {" · "}{d.product_ids?.length ? products.filter((p) => d.product_ids.includes(p.id)).map((p) => p.name).join(", ") || `${d.product_ids.length} account(s)` : "All accounts"}
              </span>
            </p>
            <div className="grid grid-cols-3 gap-4 border-l border-white/10 pl-4 font-mono text-xs" data-testid={`discount-stats-${d.code}`}>
              <div><p className="text-[10px] uppercase tracking-widest text-slate-400">Orders</p><p className="text-sm text-white">{codeStats?.[d.code]?.orders ?? 0}<span className="text-slate-400"> ({codeStats?.[d.code]?.paid_orders ?? 0} paid)</span></p></div>
              <div><p className="text-[10px] uppercase tracking-widest text-slate-400">Brought in</p><p className="text-sm text-[#e9d5ff]">${(codeStats?.[d.code]?.revenue ?? 0).toFixed(2)}</p></div>
              <div><p className="text-[10px] uppercase tracking-widest text-slate-400">Discount given</p><p className="text-sm text-[#ff7ad9]">${(codeStats?.[d.code]?.discount_given ?? 0).toFixed(2)}</p></div>
            </div>
            <label className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-slate-400">
              <Switch checked={d.active} onCheckedChange={() => toggle(d)} data-testid={`discount-active-${d.code}`} /> {d.active ? "Live" : "Off"}
            </label>
            <button onClick={() => remove(d)} className="p-2 text-slate-300 hover:text-[#ff3dc8]" aria-label="Delete code" data-testid={`discount-delete-${d.code}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};
